import TurndownService from "turndown";

// define tinymce body content style as inline style
const content_style = (
  "body { font-family: Arial; font-size: 14px; } " +
  "body > p, body > pre { margin: 5px 0 6px 0; }"
);

export const initialProps = () => {
  return {
    skin: false,
    content_css: false,
    content_style,
    height: 300,
    min_height: 250,
    max_height: 550,
    menubar: '',
    default_link_target: '_blank',
    link_title: false,
    target_list: false,
    custom_colors: false,
    plugins: 'link emoticons lists',
    extended_valid_elements: 'custom_inline[style]',
    custom_elements: '~custom_inline', // in order to know after what to parse for "`" and "```"
    valid_children: 'pre[br|#text],custom_inline[#text],span[#text]',
    toolbar: 'bold italic strikethrough | ' +
      'link | ' +
      /*'numlist bullist | ' +*/
      'blockquote | ' +
      'code_line code_block | ' +
      'removeformat | ' +
      'emoticons',
    // not sure about `branding`, but this software is under MIT license
    branding: false,
    paste_as_text: true,
    promotion: false,
    statusbar: false,
    setup: (editor: any) => {
      /*
       * Add `text` and ```text``` options
       */
      editor.ui.registry.addButton('code_line', {
        icon: 'code-sample',
        tooltip: 'Insert code line',
        onAction: function (event: any) {
          editor.insertContent('<p>' +
            '<custom_inline style="background-color: #f8f8f8; color: #e01e5a;">' +
            'line of code</custom_inline>&nbsp;' +
            '</p>');
        }
      });

      editor.ui.registry.addButton('code_block', {
        icon: 'sourcecode',
        tooltip: 'Insert code block',
        onAction: function (event: any) {
          editor.insertContent('<pre style="background-color: #f8f8f8; ">block of code</pre>');
        }
      });
    },
    init_instance_callback: (editor: any) => {

    }
  }
};

// see https://github.com/mixmark-io/turndown/blob/v7.1.2/src/turndown.js#L7
const escapes = [
  [/\\/g, '\\\\'],
  [/\*/g, '\\*'],
  [/^-/g, '\\-'],
  [/^\+ /g, '\\+ '],
  [/^(=+)/g, '\\$1'],
  [/^(#{1,6}) /g, '\\$1 '],
  [/`/g, '\\`'],
  [/^~~~/g, '\\~~~'],
  // [/\[/g, '\\['], // e.g. query_1.data()[0]
  // [/\]/g, '\\]'], // e.g. query_1.data()[0]
  [/^>/g, '\\>'],
  // [/_/g, '\\_'], // e.g. parameter_1
  [/^(\d+)\. /g, '$1\\. ']
]

export const convertHtmlToSlackMarkdown = (html: string) => {
  const turndownService = new TurndownService();

  // see https://github.com/mixmark-io/turndown/blob/v7.1.2/src/turndown.js#L142
  turndownService.escape = (str: string) => {
    return escapes.reduce(function (accumulator, escape) {
      return accumulator.replace(escape[0], escape[1] as any)
    }, str)
  }

  // Create a DOM parser to parse the HTML
  const parser = new DOMParser();
  const doc = parser.parseFromString(html, 'text/html');

  // Links
  turndownService.addRule('slackLinks', {
    filter: 'a',
    replacement: (content: string, node: any) => {
      return `<${(node as Element).getAttribute('href')}|${content}>`;
    }
  });

  // Convert headings to bold
  turndownService.addRule('headings', {
    filter: ['h1', 'h2', 'h3', 'h4', 'h5', 'h6'],
    replacement: (content: string) => `*${content}*`
  });

  // Bold
  turndownService.addRule('slackBold', {
    filter: ['strong', 'b'],
    replacement: (content: string) => `*${content}*`
  });

  // Italic
  turndownService.addRule('slackItalic', {
    filter: ['em', 'i'],
    replacement: (content: string) => `_${content}_`
  });

  // Strikethrough
  turndownService.addRule('slackStrike', {
    filter: ['s'],
    replacement: (content: string) => `~${content}~`
  });

  // Unordered lists
  turndownService.addRule('slackUl', {
    filter: 'ul',
    replacement: (content: string) => {
      // Splitting by '\n' and mapping to add bullet points
      return content
        .split('\n')
        .map(item => `• ${item.replace(/^\s*\*\s*/, '')}`)
        .join('\n');
    }
  });

  // Ordered lists
  turndownService.addRule('slackOl', {
    filter: (node: Element) => node.nodeName === 'OL',
    replacement: (content: string, node: any) => {
      // Splitting by '\n' and mapping to add numbering
      return Array.from(node.children).map((child: any, index) => {
        return `${index + 1}. ${child.textContent}`;
      }).join('\n');
    }
  });

  // Pre-formatted text
  turndownService.addRule('slackPre', {
    filter: 'pre',
    replacement: (content: any) => `\`\`\`${content}\`\`\``
  });

  // Block quotes
  turndownService.addRule('slackBlockquote', {
    filter: 'blockquote',
    replacement: (content: string) => {
      return content
        .split('\n')
        .map(line => `> ${line}`)
        .join('\n');
    }
  });

  let markdown = turndownService.turndown(doc.body);

  // Post-process step to replace escaped mentions
  markdown = markdown.replace(/<@([a-zA-Z0-9_]+)\\_([a-zA-Z0-9_]+)>/g, "<@$1_$2>");

  // Post-process step to unescape asterisks
  markdown = markdown.replace(/\\\*/g, "*");

  return markdown;
};
